import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Zap, Code2, Database, CreditCard, Shield, ArrowRight,
  Check, Cpu, Wand2, Rocket, Clock, LineChart, Star
} from "lucide-react";
import { PLAN_TIERS } from "@/lib/plans";

const HOW_STEPS = [
  { icon: Wand2, title: "Décrivez votre idée", desc: "Racontez votre concept en langage naturel — comme si vous parliez à un cofondateur." },
  { icon: Cpu, title: "L'IA architecture tout", desc: "Design, base de données, authentification, endpoints API et intégration Stripe : générés en quelques secondes." },
  { icon: Rocket, title: "Récupérez le blueprint", desc: "Cahier des charges complet, schéma DB, roadmap MVP. Prêt à lancer ou déléguer." },
];

const FEATURES = [
  { icon: Sparkles, title: "IA Claude Sonnet 5", desc: "Un des modèles les plus performants pour générer une architecture produit cohérente." },
  { icon: Database, title: "Schéma de données", desc: "Tables, relations et endpoints REST proposés automatiquement." },
  { icon: CreditCard, title: "Stripe intégré", desc: "Modèle économique et prix suggérés en accord avec votre marché cible." },
  { icon: Shield, title: "Authentification prête", desc: "JWT, sessions, OAuth : la spec est incluse dans chaque projet généré." },
  { icon: LineChart, title: "Roadmap MVP", desc: "5 étapes claires pour passer du blueprint à votre premier client payant." },
  { icon: Clock, title: "En moins de 30 secondes", desc: "Ce qui prend une semaine à un consultant se génère en un café." },
];

const TESTIMONIALS = [
  { name: "Léa Moreau", role: "Fondatrice, LinkFlow", quote: "En une soirée j'avais le blueprint complet de mon micro-SaaS. J'ai lancé en 2 semaines.", mrr: "MRR : 2 400 €" },
  { name: "Thomas Girard", role: "Co-founder, InvoiceAI", quote: "AutoSaaS m'a évité de payer 6 000 € à une agence pour cadrer le projet. Vraiment.", mrr: "MRR : 5 100 €" },
  { name: "Amina Benali", role: "Product Owner, Nomad-CRM", quote: "Le schéma de base de données proposé était bluffant de précision.", mrr: "MRR : 8 900 €" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const goStart = () => navigate(user ? "/generer" : "/inscription");

  return (
    <div className="App min-h-screen relative">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8" data-testid="hero-badge">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-mono uppercase tracking-widest text-gray-300">Propulsé par Claude Sonnet 5 & Stripe</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight" data-testid="hero-title">
                Décrivez votre idée. <br />
                <span className="text-gradient">L'IA crée votre SaaS complet.</span>
              </h1>

              <p className="mt-6 text-lg text-gray-400 max-w-xl leading-relaxed" data-testid="hero-subtitle">
                Design, base de données, authentification, endpoints API, intégration Stripe et roadmap MVP.
                Tout est généré à partir d'une simple description en français — sans écrire une seule ligne de code.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <button onClick={goStart} className="pill-primary flex items-center gap-2" data-testid="hero-cta-start">
                  Démarrer gratuitement <ArrowRight size={18} />
                </button>
                <a href="#tarifs" className="pill-ghost flex items-center gap-2" data-testid="hero-cta-pricing">
                  Voir les tarifs
                </a>
              </div>

              <div className="mt-10 flex items-center gap-6 text-xs text-gray-500 font-mono uppercase tracking-widest">
                <span>+ 1 200 fondateurs</span>
                <span>·</span>
                <span>GDPR-ready</span>
                <span>·</span>
                <span>Sans carte</span>
              </div>
            </div>

            {/* Preview card */}
            <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: '150ms' }}>
              <div className="glass rounded-2xl p-6 relative overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-3 h-3 rounded-full bg-red-400/70" />
                  <span className="w-3 h-3 rounded-full bg-amber-400/70" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400/70" />
                  <span className="ml-2 text-xs font-mono text-gray-400">autosaas — génération en direct</span>
                </div>
                <div className="space-y-3 font-mono text-xs">
                  <div className="text-cyan-300">$ prompt&gt; Une marketplace pour freelances IA</div>
                  <div className="text-gray-400">→ Analyse du concept…</div>
                  <div className="text-gray-400">→ Sélection stack : React + FastAPI + MongoDB</div>
                  <div className="text-gray-400">→ Schéma DB : 6 tables générées</div>
                  <div className="text-gray-400">→ 12 endpoints API définis</div>
                  <div className="text-indigo-300">→ Blueprint prêt <Check size={12} className="inline" /></div>
                  <div className="shimmer h-3 w-full rounded" />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {["Design", "Base de données", "Stripe"].map((t) => (
                    <div key={t} className="rounded-xl p-3 border border-white/5 bg-white/[0.02] text-center">
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono">{t}</div>
                      <div className="text-sm text-emerald-300 mt-1 flex items-center justify-center gap-1"><Check size={12} /> OK</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-glow max-w-5xl mx-auto" />

      {/* HOW IT WORKS */}
      <section id="comment" className="py-24" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-indigo-400">Étape par étape</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3">Trois étapes. Zéro ligne de code.</h2>
            <p className="text-gray-400 mt-4">De l'idée floue au blueprint exploitable en moins d'une minute.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {HOW_STEPS.map((step, i) => (
              <div key={step.title} className="glass card-hover rounded-2xl p-8 relative" data-testid={`how-step-${i + 1}`}>
                <div className="text-6xl font-black text-white/[0.04] absolute top-2 right-4 font-mono">0{i + 1}</div>
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                  <step.icon size={22} className="text-indigo-400" />
                </div>
                <h3 className="mt-6 text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="py-24 bg-[#0A0E15]/50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-400">Ce que l'IA livre</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3">Un blueprint complet, pas un simple pitch.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="glass card-hover rounded-2xl p-6" data-testid={`feature-card-${f.title}`}>
                <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <f.icon size={18} className="text-cyan-300" />
                </div>
                <h3 className="mt-5 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="tarifs" className="py-24" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-amber-400">Tarification simple</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3">Choisissez votre cadence de création</h2>
            <p className="text-gray-400 mt-4">Sans engagement. Résiliez en un clic.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {PLAN_TIERS.map((tier) => (
              <div
                key={tier.key}
                className={`relative rounded-2xl p-8 border card-hover ${tier.popular ? 'border-indigo-500/40 bg-gradient-to-b from-indigo-500/10 to-transparent' : 'glass'}`}
                data-testid={`pricing-card-${tier.key}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-indigo-500 text-white text-xs font-semibold uppercase tracking-wider">
                    Le plus choisi
                  </div>
                )}
                <div className="text-sm font-mono uppercase tracking-widest text-gray-400">{tier.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold">{tier.price}</span>
                  <span className="text-gray-500">€/mois</span>
                </div>
                <div className="mt-2 text-sm text-indigo-300">{tier.quota}</div>
                <ul className="mt-6 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goStart}
                  className={`mt-8 w-full ${tier.popular ? 'pill-primary' : 'pill-ghost'}`}
                  data-testid={`pricing-cta-${tier.key}`}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="temoignages" className="py-24 bg-[#0A0E15]/50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400">Fondateurs no-code</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3">Ils ont lancé leur SaaS sans coder.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="glass card-hover rounded-2xl p-6" data-testid={`testimonial-${t.name}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-gray-300 leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                  <div className="text-xs font-mono text-emerald-400">{t.mrr}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="glass rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-30" />
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Votre prochain SaaS commence par une phrase.</h2>
              <p className="text-gray-400 mt-4 max-w-xl mx-auto">Rejoignez 1 200+ fondateurs qui ont transformé une idée en produit lançable, sans ligne de code.</p>
              <button onClick={goStart} className="mt-8 pill-primary inline-flex items-center gap-2" data-testid="final-cta-button">
                Générer mon premier SaaS <Zap size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-gray-500">
        © 2026 AutoSaaS.ai — Fait avec obsession pour les fondateurs non-tech.
      </footer>
    </div>
  );
}
